import type {
  Action,
  ActionSectionItem,
  AnySection,
  AnySectionItem,
  Category,
  CategoryBase,
  CustomClickAndDropHandler,
  MediaManagerOptions,
  PresetListSectionItem,
  PresetSectionItem,
  RelatedApp,
  Structure,
} from '@wix/add-panel-common';
import type { MouseEvent } from 'react';
import type { FedopsInteraction } from '../../constants/FedopsInteraction';
import type { PathsState } from '../AddPanel.types';
import type {
  AddComponentAction as SavedComponentsAddComponentAction,
  DeleteComponentAction as SavedComponentsDeleteItemAction,
  FetchCollectionAction as SavedComponentsFetchCollectionAction,
  RenameComponentAction as SavedComponentsRenameItemAction,
  SavedComponentAppearedAction as SavedComponentsFetchItemPreview,
  StartComponentDragAction as SavedComponentsStartComponentDragAction,
  SavedComponentsFooterCTAClick,
} from './useSavedComponentActionsDispatcher/useSavedComponentActionsDispatcher.types';

export interface Context {
  dispatchAction: DispatchActionFn;
}

export interface ActionsDispatcherProviderOuter {
  onAction: Context['dispatchAction'];
}

export type ActionsDispatcherFn = () => void;
export type ActionsDispatcherFnWithPayload<T extends Required<{ payload: unknown }>> = (payload: T['payload']) => void;

export type UseGenericActionsDispatcher = (type: Action, payload: GenericActionPayload) => void;

export interface UseActionsDispatcher {
  dispatchCategoryClick: ActionsDispatcherFnWithPayload<CategoryClickAction>;
  dispatchCloseAddPanel: ActionsDispatcherFn;
  dispatchFedopsInteractionStarted: ActionsDispatcherFnWithPayload<FedopsInteractionStartedAction>;
  dispatchFedopsInteractionEnded: ActionsDispatcherFnWithPayload<FedopsInteractionEndedAction>;
  dispatchHelpClick: ActionsDispatcherFnWithPayload<HelpClickAction>;
  dispatchCategoryOpen: ActionsDispatcherFnWithPayload<CategoryOpenAction>;
  dispatchSectionOpen: ActionsDispatcherFnWithPayload<SectionOpenAction>;
  dispatchSectionClick: ActionsDispatcherFnWithPayload<SectionClickAction>;
  dispatchUnmount: ActionsDispatcherFnWithPayload<PanelUnmountAction>;
  dispatchPlayVideo: ActionsDispatcherFnWithPayload<PlayVideoAction>;
  dispatchComponentClick: ActionsDispatcherFnWithPayload<ComponentClickAction>;
  dispatchComponentMouseEnter: ActionsDispatcherFnWithPayload<ComponentMouseEnter>;
  dispatchComponentMouseLeave: ActionsDispatcherFnWithPayload<ComponentMouseLeave>;
  dispatchMouseUp: ActionsDispatcherFnWithPayload<MouseUpAction>;
  dispatchPathUpdate: ActionsDispatcherFnWithPayload<PathUpdateAction>;
  dispatchStartItemDrag: ActionsDispatcherFnWithPayload<StartItemDragAction>;
  dispatchOpenMediaManager: ActionsDispatcherFnWithPayload<OpenMediaManagerAction>;
  dispatchAddBlankPage: ActionsDispatcherFnWithPayload<AddBlankPageAction>;
  dispatchAddPreset: ActionsDispatcherFnWithPayload<AddPresetAction>;
  dispatchAddCollection: ActionsDispatcherFn;
  dispatchAddDataSet: ActionsDispatcherFnWithPayload<AddDataSetAction>;
  dispatchAddFormDataSet: ActionsDispatcherFnWithPayload<AddFormDataSetAction>;
  dispatchInstallAppAndNotify: ActionsDispatcherFnWithPayload<InstallAppAndNotifyAction>;
  dispatchAddRelatedApp: ActionsDispatcherFnWithPayload<AddRelatedAppAction>;
  dispatchDragRelatedApp: ActionsDispatcherFnWithPayload<DragRelatedAppAction>;
  dispatchOpenWixAppMarket: ActionsDispatcherFnWithPayload<OpenWixAppMarketAction>;
  dispatchInstallSuperApp: ActionsDispatcherFnWithPayload<InstallSuperAppAction>;
  dispatchSearchButtonClick: ActionsDispatcherFn;
  dispatchToggleDevMode: ActionsDispatcherFnWithPayload<ToggleDevModeAction>;
  dispatchOpenAccountSettings: ActionsDispatcherFnWithPayload<OpenAccountSettingsAction>;
  dispatchCreateNewForm: ActionsDispatcherFn;
  dispatchAddExistingForm: ActionsDispatcherFn;
  dispatchReloadData: ActionsDispatcherFn;
  dispatchAddQuickActionBar: ActionsDispatcherFnWithPayload<AddQuickActionBarAction>;
  dispatchAddBackToTopButton: ActionsDispatcherFnWithPayload<AddBackToTopButtonAction>;
  dispatchAddWelcomeScreen: ActionsDispatcherFnWithPayload<AddWelcomeScreenAction>;
  dispatchOpenPhotoStudio: ActionsDispatcherFnWithPayload<OpenPhotoStudioAction>;
  dispatchAddChromeThemeColor: ActionsDispatcherFnWithPayload<AddChromeThemeColorAction>;
  dispatchOpenLeftbarSidePanelAction: ActionsDispatcherFnWithPayload<OpenLeftbarSidePanelAction>;
  dispatchStartMyWidgetDrag: ActionsDispatcherFnWithPayload<StartMyWidgetDragAction>;
}

interface CloseAddPanelAction {
  type: Action.CloseAddPanel;
}

interface CategoryClickAction {
  type: Action.CategoryClick;
  payload: {
    categoryId: string;
    biParams: {
      category_under_scroll: boolean;
      isScrollable: boolean | null;
      window_width: number;
      window_height: number;
    };
  };
}

interface CategoryOpenAction {
  type: Action.CategoryOpen;
  payload: {
    categoryId: string;
    sectionName?: string;
    bi: {
      is_vertical_installed: boolean | null;
      category_under_scroll: boolean;
      isScrollable: boolean | null;
      window_width: number;
      window_height: number;
    };
  };
}

interface SectionOpenAction {
  type: Action.SectionOpen;
  payload: {
    categoryId: string;
    sectionName: string;
  };
}

interface SectionClickAction {
  type: Action.SectionClick;
  payload: {
    categoryId: string;
    sectionName: string;
  };
}

interface FedopsInteractionStartedAction {
  type: Action.FedopsInteractionStarted;
  payload: {
    interaction: FedopsInteraction;
  };
}

interface FedopsInteractionEndedAction {
  type: Action.FedopsInteractionEnded;
  payload: {
    interaction: FedopsInteraction;
  };
}

export enum HelpActionInitiator {
  CategoryViewHeader = 'categoryViewHeader',
  SectionHeader = 'sectionHeader',
  ActionSectionItemSubtitle = 'actionSectionItemSubtitle',
  WelcomeSection = 'welcomeSection',
  ErrorState = 'errorState',
}

interface HelpClickAction {
  type: Action.HelpClick;
  payload: {
    helpId: string;
    categoryId: string;
    initiator: HelpActionInitiator;
    biHelpParams: {
      origin: string;
      component: string;
      panel_name: string;
      learn_more: boolean;
    };
  };
}

interface PlayVideoAction {
  type: Action.PlayVideo;
  payload: {
    title: string;
  };
}

interface ComponentClickAction {
  type: Action.ComponentClick;
  payload: {
    itemId?: string;
    category: CategoryBase;
    section: AnySection;
    customClickAndDropHandler?: CustomClickAndDropHandler;
    item?: PresetSectionItem | PresetListSectionItem | ActionSectionItem;
    eventOrigin?: { clientX: number; clientY: number };
    compDef?: Structure;
  };
}

export interface PresetSectionComponentMetaData {
  itemIndex?: number;
  sectionIndex?: number;
  sectionName: string;
  categoryId: string;
}

interface ComponentMouseEnter {
  type: Action.ComponentMouseEnter;
  payload: {
    itemId: string;
    compDef: Structure;
    metaData: PresetSectionComponentMetaData;
  };
}

interface ComponentMouseLeave {
  type: Action.ComponentMouseLeave;
  payload: {
    itemId: string;
  };
}

interface MouseUpAction {
  type: Action.MouseUp;
  payload: { event: MouseEvent };
}

interface PanelUnmountAction {
  type: Action.Unmount;
  payload: {
    pathsState: PathsState;
  };
}

interface PathUpdateAction {
  type: Action.PathUpdate;
  payload: {
    path: string[];
  };
}

export interface DragItemRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface StartItemDragAction {
  type: Action.StartItemDrag;
  payload: {
    event: MouseEvent;
    categoryId: string;
    section: AnySection;
    rect: DragItemRect;
    item?: AnySectionItem;
    itemId: string;
    itemStructure: Structure;
  };
}

interface OpenMediaManagerAction {
  type: Action.OpenMediaManager;
  payload: {
    categoryId: string;
    sectionName: string;
    sectionTitle: string;
    biItemName?: string;
    mediaManagerOptions: MediaManagerOptions;
  };
}

interface AddPresetAction {
  type: Action.AddPreset;
  payload: {
    item: ActionSectionItem;
  };
}

interface AddCollectionAction {
  type: Action.AddCollection;
}

interface AddDataSetAction {
  type: Action.AddDataSet;
  payload: {
    item: ActionSectionItem;
  };
}

interface AddFormDataSetAction {
  type: Action.AddFormDataSet;
  payload: {
    item: ActionSectionItem;
  };
}

interface AddBlankPageAction {
  type: Action.AddBlankPage;
  payload: {
    item: ActionSectionItem;
  };
}

interface AddQuickActionBarAction {
  type: Action.AddQuickActionBar;
  payload: {
    item: ActionSectionItem;
  };
}

interface AddBackToTopButtonAction {
  type: Action.AddBackToTopButton;
  payload: {
    item: ActionSectionItem;
  };
}

interface AddWelcomeScreenAction {
  type: Action.AddWelcomeScreen;
  payload: {
    item: ActionSectionItem;
  };
}

interface OpenPhotoStudioAction {
  type: Action.OpenPhotoStudio;
  payload: {
    item: ActionSectionItem;
  };
}

interface AddChromeThemeColorAction {
  type: Action.AddChromeThemeColor;
  payload: {
    item: ActionSectionItem;
  };
}

interface InstallAppAndNotifyAction {
  type: Action.InstallAppAndNotify;
  payload: {
    item: ActionSectionItem;
  };
}

interface GenericActionPayload {
  section: AnySection;
  category: Category;
}

interface AddRelatedAppAction {
  type: Action.AddRelatedApp;
  payload: {
    app: RelatedApp;
    categoryId: string;
  };
}

interface DragRelatedAppAction {
  type: Action.DragRelatedApp;
  payload: {
    event: MouseEvent;
    categoryId: string;
    sectionTitle: string;
    app: RelatedApp;
    dragItemData: {
      rect: DragItemRect;
      appItem: {
        structure: {
          layout: {
            width: number;
            height: number;
          };
        };
      };
    };
  };
}

interface StartMyWidgetDragAction {
  type: Action.MyWidgetsStartDrag;
  payload: {
    event: MouseEvent;
    sectionName: string;
    dragItemData: {
      rect: DragItemRect;
      item: {
        itemId: string;
        onDrop: () => void;
        structure: {
          layout: {
            width: number;
            height: number;
          };
        };
      };
    };
  };
}

interface OpenWixAppMarketAction {
  type: Action.OpenWixAppMarket;
  payload: {
    categoryId: string;
    referralInfo: string;
  };
}

interface InstallSuperAppAction {
  type: Action.InstallSuperApp;
  payload: {
    appDefinitionId: string;
  };
}

interface ClickSearchButtonAction {
  type: Action.SearchButtonClick;
}

interface ToggleDevModeAction {
  type: Action.ToggleDevMode;
  payload: {
    categoryId: string;
    sectionTitle: string;
    itemId: string;
  };
}

interface OpenAccountSettingsAction {
  type: Action.OpenAccountSettings;
  payload: {
    path: string;
    itemId: string;
    categoryId: string;
    sectionTitle: string;
  };
}

interface CreateNewFormAction {
  type: Action.CreateNewForm;
}

interface AddExistingFormAction {
  type: Action.AddExistingForm;
}

interface ReloadDataAction {
  type: Action.ReloadData;
}

interface OpenLeftbarSidePanelAction {
  type: Action.OpenLeftbarSidePanel;
  payload: {
    sideBarComponent: React.ReactNode;
  };
}

export interface DispatchActionFn {
  (action: CategoryClickAction): void;
  (action: HelpClickAction): void;
  (action: PlayVideoAction): void;
  (action: CloseAddPanelAction): void;
  (action: FedopsInteractionStartedAction): void;
  (action: FedopsInteractionEndedAction): void;
  (action: PanelUnmountAction): void;
  (action: CategoryOpenAction): void;
  (action: SectionOpenAction): void;
  (action: SectionClickAction): void;
  (action: ComponentClickAction): void;
  (action: ComponentMouseEnter): void;
  (action: ComponentMouseLeave): void;
  (action: MouseUpAction): void;
  (action: PathUpdateAction): void;
  (action: StartItemDragAction): void;
  (action: OpenMediaManagerAction): void;
  (action: AddBlankPageAction): void;
  (action: AddQuickActionBarAction): void;
  (action: AddBackToTopButtonAction): void;
  (action: AddWelcomeScreenAction): void;
  (action: OpenPhotoStudioAction): void;
  (action: AddChromeThemeColorAction): void;
  (action: AddPresetAction): void;
  (action: AddCollectionAction): void;
  (action: AddDataSetAction): void;
  (action: AddFormDataSetAction): void;
  (action: InstallAppAndNotifyAction): void;
  (action: SavedComponentsFetchCollectionAction): void;
  (action: SavedComponentsFetchItemPreview): void;
  (action: SavedComponentsAddComponentAction): void;
  (action: SavedComponentsRenameItemAction): void;
  (action: SavedComponentsDeleteItemAction): void;
  (action: SavedComponentsStartComponentDragAction): void;
  (action: SavedComponentsFooterCTAClick): void;
  (action: { type: Action; payload: GenericActionPayload }): void;
  (action: AddRelatedAppAction): void;
  (action: DragRelatedAppAction): void;
  (action: OpenWixAppMarketAction): void;
  (action: InstallSuperAppAction): void;
  (action: ClickSearchButtonAction): void;
  (action: ToggleDevModeAction): void;
  (action: OpenAccountSettingsAction): void;
  (action: ReloadDataAction): void;
  (action: CreateNewFormAction): void;
  (action: AddExistingFormAction): void;
  (action: OpenLeftbarSidePanelAction): void;
  (action: StartMyWidgetDragAction): void;
}

export type DispatchableAction =
  | CategoryClickAction
  | HelpClickAction
  | PlayVideoAction
  | CloseAddPanelAction
  | FedopsInteractionStartedAction
  | FedopsInteractionEndedAction
  | PanelUnmountAction
  | CategoryOpenAction
  | SectionOpenAction
  | ComponentClickAction
  | ComponentMouseEnter
  | ComponentMouseLeave
  | MouseUpAction
  | PathUpdateAction
  | StartItemDragAction
  | OpenMediaManagerAction
  | AddBlankPageAction
  | AddPresetAction
  | AddCollectionAction
  | AddDataSetAction
  | AddFormDataSetAction
  | InstallAppAndNotifyAction
  | SavedComponentsFetchCollectionAction
  | SavedComponentsFetchItemPreview
  | SavedComponentsAddComponentAction
  | SavedComponentsRenameItemAction
  | SavedComponentsDeleteItemAction
  | SavedComponentsStartComponentDragAction
  | AddRelatedAppAction
  | DragRelatedAppAction
  | OpenWixAppMarketAction
  | InstallSuperAppAction
  | ClickSearchButtonAction
  | ToggleDevModeAction
  | OpenAccountSettingsAction
  | ReloadDataAction
  | CreateNewFormAction
  | AddExistingFormAction
  | OpenLeftbarSidePanelAction;
